<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import FormSelect from '@/components/Atoms/FormSelect.vue'
import FormInput from '@/components/Atoms/FormInput.vue'

// Font family for the site (runtime `settings.fontFamily`, ≤ 120 chars,
// letters / digits / quotes / commas only — see ott-cms sanitizeFontFamily).
// The dropdown offers a curated Google Fonts list; "Custom…" reveals the
// free-text input for anything else. The value is stored as a CSS
// `font-family` list ("Open Sans", sans-serif). The runtime applies it as
// `--ds-font-family`; loading the font file itself (css2 stylesheet) is the
// runtime's job — the dashboard only loads it for the preview below.
const props = defineProps({
	modelValue: {
		type: String,
		default: '',
	},
	id: {
		type: String,
		default: 'site-font',
	},
	label: {
		type: String,
		default: 'Font family',
	},
	// Family the runtime falls back to when nothing is saved (KV theme seed).
	seededValue: {
		type: String,
		default: '',
	},
})
const emit = defineEmits(['update:modelValue'])

const SYSTEM_STACK = 'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
const SYSTEM = 'system'
const CUSTOM = 'custom'

// [family, generic fallback]
const GOOGLE_FONTS = [
	['Inter', 'sans-serif'],
	['Roboto', 'sans-serif'],
	['Open Sans', 'sans-serif'],
	['Lato', 'sans-serif'],
	['Montserrat', 'sans-serif'],
	['Poppins', 'sans-serif'],
	['Nunito', 'sans-serif'],
	['Source Sans 3', 'sans-serif'],
	['Work Sans', 'sans-serif'],
	['DM Sans', 'sans-serif'],
	['Space Grotesk', 'sans-serif'],
	['Playfair Display', 'serif'],
	['Merriweather', 'serif'],
	['Oswald', 'sans-serif'],
]

/** CSS `font-family` value the runtime stores: quoted when the name needs it. */
const cssValue = (family, generic) => `${/[^A-Za-z-]/.test(family) ? `"${family}"` : family}, ${generic}`
/** First family name of a stored value, unquoted. */
const firstFamily = (value) => String(value || '').split(',')[0].trim().replace(/^["']|["']$/g, '')

const options = computed(() => [
	{ value: '', text: props.seededValue ? `Site default (${firstFamily(props.seededValue)})` : 'System default' },
	...GOOGLE_FONTS.map(([family, generic]) => ({ value: family, text: `${family}${generic === 'serif' ? ' · serif' : ''}` })),
	{ value: SYSTEM, text: 'System font stack' },
	{ value: CUSTOM, text: 'Custom…' },
])

/** Map a stored value onto a dropdown choice. */
function choiceFor(value) {
	const v = String(value || '').trim()
	if (!v) return ''
	if (v === SYSTEM_STACK) return SYSTEM
	const family = firstFamily(v)
	if (GOOGLE_FONTS.some(([f]) => f.toLowerCase() === family.toLowerCase())) return GOOGLE_FONTS.find(([f]) => f.toLowerCase() === family.toLowerCase())[0]
	return CUSTOM
}

const choice = ref(choiceFor(props.modelValue))
const custom = ref(choice.value === CUSTOM ? props.modelValue : '')

watch(() => props.modelValue, (v) => {
	const next = choiceFor(v)
	if (next !== choice.value) choice.value = next
	if (next === CUSTOM) custom.value = v
})

const onChoice = (next) => {
	choice.value = next
	if (next === CUSTOM) {
		emit('update:modelValue', custom.value || '')
		return
	}
	if (next === SYSTEM) return emit('update:modelValue', SYSTEM_STACK)
	if (!next) return emit('update:modelValue', '')
	const [family, generic] = GOOGLE_FONTS.find(([f]) => f === next)
	emit('update:modelValue', cssValue(family, generic))
}
const onCustom = (v) => {
	custom.value = v
	emit('update:modelValue', v)
}

// The runtime accepts family names, quotes, commas and generic keywords only.
const customError = computed(() => {
	if (choice.value !== CUSTOM || !custom.value.trim()) return null
	if (custom.value.trim().length > 120) return 'Keep it under 120 characters'
	if (!/^[A-Za-z0-9 ,'"_-]+$/.test(custom.value.trim())) return 'Only letters, digits, spaces, quotes and commas'
	return null
})

// Preview: pull the selected Google family into the dashboard on demand.
const loaded = new Set()
function ensureFontLoaded(family) {
	if (!family || loaded.has(family) || typeof document === 'undefined') return
	loaded.add(family)
	const link = document.createElement('link')
	link.rel = 'stylesheet'
	link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, '+')}:wght@400;600&display=swap`
	document.head.appendChild(link)
}
const previewFamily = computed(() => {
	if (choice.value === CUSTOM) return customError.value ? '' : custom.value.trim()
	if (choice.value === SYSTEM) return SYSTEM_STACK
	if (!choice.value) return props.seededValue || SYSTEM_STACK
	const entry = GOOGLE_FONTS.find(([f]) => f === choice.value)
	return entry ? cssValue(entry[0], entry[1]) : ''
})
watch(choice, (c) => { if (GOOGLE_FONTS.some(([f]) => f === c)) ensureFontLoaded(c) }, { immediate: true })
onMounted(() => { if (props.seededValue) ensureFontLoaded(firstFamily(props.seededValue)) })

defineExpose({ customError })
</script>

<template>
	<div class="min-w-0" :data-testid="`${id}-field`">
		<FormSelect :id="id" :model-value="choice" :label="label" :options="options" @update:model-value="onChoice" />
		<div v-if="choice === CUSTOM" class="mt-2">
			<FormInput :id="`${id}-custom`" :model-value="custom" placeholder="e.g. Figtree, sans-serif" :maxlength="120" :is-error="Boolean(customError)"
				aria-label="Custom font family" @update:model-value="onCustom" />
			<p class="mb-0 mt-1.5 text-tiny" :class="customError ? 'text-error' : 'text-surface-7'">
				{{ customError || 'A CSS font-family list, e.g. "Figtree", sans-serif. Use a Google Fonts family or one your site already loads.' }}
			</p>
		</div>
		<div class="mt-2 rounded-md bg-surface-3 px-3 py-2" data-testid="font-preview" :style="previewFamily ? { fontFamily: previewFamily } : undefined">
			<p class="mb-0 truncate text-base text-white">The quick brown fox jumps over the lazy dog</p>
			<p class="mb-0 truncate text-tiny text-surface-8">Aa Bb Cc 0123456789 · headings, menus and body text on your site</p>
		</div>
	</div>
</template>
